import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';

interface Profile {
  id: string;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  phone: string | null;
  location: string | null;
  company_name: string | null;
  company_logo_url: string | null;
  company_bio: string | null;
  company_website: string | null;
  company_email: string | null;
  company_phone: string | null;
  company_slug: string | null;
  company_certifications: string[] | null;
  company_founded_year: number | null;
  company_employees_range: string | null;
  represent_as_company: boolean;
  user_type: string | null;
  is_verified: boolean;
  dealer_status: string | null;
  verification_documents_count: number;
  response_time_rating: number | null;
  linkedin_profile_url: string | null;
  linkedin_verified: boolean | null;
  linkedin_verified_at: string | null;
  created_at: string;
  updated_at: string;
  last_active_at: string | null;
}

interface UpdateProfileData {
  display_name?: string;
  bio?: string;
  phone?: string;
  location?: string;
  company_name?: string;
  company_logo_url?: string;
  company_bio?: string;
  company_website?: string;
  company_email?: string;
  company_phone?: string;
  company_slug?: string;
  company_certifications?: string[];
  company_founded_year?: number;
  company_employees_range?: string;
  represent_as_company?: boolean;
  user_type?: string;
}

export const useProfile = () => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Fetch profile data
  const fetchProfile = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        setError(error.message);
        return;
      }

      setProfile(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  // Update profile
  const updateProfile = async (updates: UpdateProfileData) => {
    if (!user || !profile) {
      throw new Error('No user or profile found');
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating profile:', error);
        throw error;
      }

      setProfile(data);
      return data;
    } catch (err) {
      console.error('Error updating profile:', err);
      throw err;
    }
  };

  // Upload avatar
  const uploadAvatar = async (file: File) => {
    if (!user) {
      throw new Error('No user found');
    }

    try {
      // Delete old avatar if exists
      if (profile?.avatar_url) {
        const oldPath = profile.avatar_url.split('/').pop();
        if (oldPath) {
          await supabase.storage
            .from('listing-images')
            .remove([`avatars/${oldPath}`]);
        }
      }

      // Upload new avatar
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('listing-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        console.error('Error uploading avatar:', uploadError);
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('listing-images')
        .getPublicUrl(filePath);

      // Update profile with new avatar URL
      const { data, error: updateError } = await supabase
        .from('profiles')
        .update({
          avatar_url: publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (updateError) {
        console.error('Error updating profile with avatar URL:', updateError);
        throw updateError;
      }

      setProfile(data);
      return data;
    } catch (err) {
      console.error('Error uploading avatar:', err);
      throw err;
    }
  };

  // Upload company logo
  const uploadCompanyLogo = async (file: File) => {
    if (!user) {
      throw new Error('No user found');
    }

    try {
      // Delete old logo if exists
      if (profile?.company_logo_url) {
        const oldPath = profile.company_logo_url.split('/').pop();
        if (oldPath) {
          await supabase.storage
            .from('listing-images')
            .remove([`company-logos/${oldPath}`]);
        }
      }

      // Upload new logo
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `company-logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('listing-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        console.error('Error uploading company logo:', uploadError);
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('listing-images')
        .getPublicUrl(filePath);

      // Update profile with new logo URL
      const { data, error: updateError } = await supabase
        .from('profiles')
        .update({
          company_logo_url: publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (updateError) {
        console.error('Error updating profile with company logo URL:', updateError);
        throw updateError;
      }

      setProfile(data);
      return data;
    } catch (err) {
      console.error('Error uploading company logo:', err);
      throw err;
    }
  };

  // Real-time subscription to profile changes
  useEffect(() => {
    if (!user) return;

    fetchProfile();

    const channel = supabase
      .channel('profile-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Profile updated:', payload);
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            setProfile(payload.new as Profile);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return {
    profile,
    loading,
    error,
    updateProfile,
    uploadAvatar,
    uploadCompanyLogo,
    refetch: fetchProfile
  };
};